import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { Loader2, Pipette } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ColorPicker from "../ColorPicker";

const TagSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Nome muito curto")
		.max(50, "Nome muito longo")
		.required("Informe o nome"),
	color: Yup.string()
		.min(3, "Cor inválida")
		.max(9, "Cor inválida")
		.required("Escolha uma cor"),
});

const TagModal = ({ open, onClose, tagId }) => {
	const initialState = {
		name: "",
		// cor padrão evita bloquear o cadastro por um campo que o usuário
		// costuma não notar (só é alterado pelo seletor).
		color: "#34d399",
	};

	const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);
	const [tag, setTag] = useState(initialState);

	useEffect(() => {
		(async () => {
			if (!tagId) return;
			try {
				const { data } = await api.get(`/tags/${tagId}`);
				setTag((prevState) => {
					return { ...prevState, ...data };
				});
			} catch (err) {
				toastError(err);
			}
		})();

		// Volta ao initialState (e não a um objeto vazio) para não perder
		// a cor padrão ao reabrir o modal.
		return () => {
			setTag(initialState);
		};
	}, [tagId, open]);

	const handleClose = () => {
		onClose();
		setTag(initialState);
	};

	const handleSaveTag = async (values) => {
		try {
			if (tagId) {
				await api.put(`/tags/${tagId}`, values);
			} else {
				await api.post("/tags", values);
			}
			toast.success(i18n.t("tagModal.success"));
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>
						{tagId ? "Editar etiqueta" : "Nova etiqueta"}
					</DialogTitle>
					<DialogDescription>
						Criar a etiqueta não muda nada sozinho: ela passa a aparecer na
						lista de escolha dentro do atendimento, para a equipe colar nas
						conversas.
					</DialogDescription>
				</DialogHeader>
				<Formik
					initialValues={tag}
					enableReinitialize={true}
					validationSchema={TagSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveTag(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => (
						<Form className="space-y-4">
							<div className="space-y-1.5">
								<Label htmlFor="name">{i18n.t("tagModal.form.name")}</Label>
								<Field
									as={Input}
									id="name"
									name="name"
									autoFocus
									placeholder="Ex.: Orçamento"
								/>
								{touched.name && errors.name && (
									<p className="text-xs text-destructive">{errors.name}</p>
								)}
								<p className="text-xs leading-relaxed text-muted-foreground">
									Curto, do jeito que a equipe fala. É por este nome que você
									vai filtrar a lista de atendimentos depois.
								</p>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="color">{i18n.t("tagModal.form.color")}</Label>
								<div className="relative">
									<span
										className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 rounded-sm border"
										style={{ backgroundColor: values.color }}
									/>
									<Field
										as={Input}
										id="color"
										name="color"
										className="pl-9 pr-9"
										onFocus={() => setColorPickerModalOpen(true)}
									/>
									<button
										type="button"
										onClick={() => setColorPickerModalOpen(true)}
										className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
									>
										<Pipette className="h-4 w-4" />
									</button>
								</div>
								{touched.color && errors.color && (
									<p className="text-xs text-destructive">{errors.color}</p>
								)}
								<p className="text-xs leading-relaxed text-muted-foreground">
									É a cor da bolinha que aparece na conversa. Cores diferentes
									por assunto ajudam a bater o olho na lista e já saber o que é.
								</p>
							</div>

							<ColorPicker
								open={colorPickerModalOpen}
								currentColor={values.color}
								handleClose={() => setColorPickerModalOpen(false)}
								onChange={(color) => {
									setFieldValue("color", color);
									setTag((prev) => ({ ...prev, ...values, color }));
								}}
							/>

							<DialogFooter>
								<Button
									type="button"
									variant="outline"
									onClick={handleClose}
									disabled={isSubmitting}
								>
									{i18n.t("tagModal.buttons.cancel")}
								</Button>
								<Button type="submit" disabled={isSubmitting}>
									{isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
									{tagId ? "Salvar alterações" : "Criar etiqueta"}
								</Button>
							</DialogFooter>
						</Form>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

export default TagModal;
